import React from 'react';
import ReactDOM from 'react-dom';

/**
 * ConfirmDialog — modal for destructive actions.
 *
 * Props:
 *   open         {boolean}
 *   title        {string}
 *   message      {string | ReactNode}
 *   childCount   {number}  — if > 0, shows cascading-delete warning
 *   confirmLabel {string}  — defaults to "Delete"
 *   onConfirm    {() => void}
 *   onCancel     {() => void}
 */
export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message,
  childCount = 0,
  confirmLabel = 'Delete',
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="confirm-overlay" onClick={onCancel}>
      <div
        className="confirm-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onClick={e => e.stopPropagation()}
      >
        <h3 id="confirm-title" className="confirm-title">{title}</h3>

        {message && <p className="confirm-msg">{message}</p>}

        {childCount > 0 && (
          <p className="confirm-warning">
            ⚠️ This will also permanently delete{' '}
            <strong>{childCount} child item{childCount !== 1 ? 's' : ''}</strong>.
            This cannot be undone.
          </p>
        )}

        <div className="confirm-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger"    onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>

      <style>{`
        .confirm-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,.45);
          display: flex; align-items: center; justify-content: center;
          z-index: 9999;
        }
        .confirm-box {
          background: #fff; border-radius: 8px;
          padding: 28px 32px; max-width: 420px; width: 90%;
          box-shadow: 0 4px 24px rgba(0,0,0,.18);
        }
        .confirm-title { margin: 0 0 12px; font-size: 17px; color: #1a1a2e; }
        .confirm-msg   { margin: 0 0 12px; color: #555; font-size: 14px; line-height: 1.5; }
        .confirm-warning {
          margin: 0 0 16px; color: #7d5a00;
          background: #fff8e1; border: 1px solid #ffe082;
          border-radius: 4px; padding: 8px 12px; font-size: 13px; line-height: 1.5;
        }
        .confirm-actions { display: flex; gap: 10px; justify-content: flex-end; }
      `}</style>
    </div>,
    document.body
  );
}
